import { useCallback, useEffect, useState } from 'react'
import { Card, Col, Row } from 'react-bootstrap';
import { Box, Typography } from '@mui/material'
import ProductTypesApi from 'api/setting/default-products/product-type.api'
import _ from 'lodash'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** STYLE */
import useStyles from 'features/warehouses/materials/style'

/** API */
import { routeName } from 'routes/routes-name'
import MaterialsApi from 'api/warehouses/material.api'

/** UTILS */
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { swalActive } from 'component/Alert/Swal'
import ButtonCustom from 'component/Button/ButtonCustom'
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputTextSearch from 'component/Input/InputTextSearch'
import TableCustom from 'component/Table/TableCustom'
import { notiSuccess, notiError } from 'component/notifications/notifications'
import TableRowCommon from 'component/Table/TableRowCommon'
import { numberFormat } from 'utils/app.utils'
import TableRowExport from 'component/Pdf/TableRowExport'
import ButtonExport from 'component/Button/ButtonExport'
import AutocompleteSelect from 'component/Select/AutocompleteSelect';
import { dateTimeToView } from 'utils/date.utils';
import AlertModal from 'new-components/modal/alert-modal';
import SnackbarComponent from 'new-components/snackbar/snackbar-component';

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

type StatusProps = {
  status: 'ACTIVE' | 'INACTIVE' | 'ALL'
  name: string
}

export default function Materials() {
  const classes = useStyles()
  const { t } = useTranslation()

  const [materials, setMaterials] = useState([])
  const [exportData, setExportData] = useState([])

  const [materialTypes, setMaterialTypes] = useState([]);
  const [materialType, setMaterialType]: any = useState({});
  const [medTypeLoading, setMedTypeLoading] = useState(false);

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('productCode')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [statusMAT, setStatusMAT] = useState<StatusProps>({ status: "ALL", name: t('ALL') })

  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const loadData = useCallback(async () => {
    let condition: any = { group: 'MATERIAL', status: '' }
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (search || !_.isEmpty(materialType)) condition = { ...condition, search: search || materialType?.productTypeName || '' }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }
    if (statusMAT) condition = { ...condition, status: statusMAT.status === "ALL" ? '' : statusMAT.status }
    const res = await MaterialsApi.findAll(condition)
    if (res.status === 200) {
      setRowCount(res.headers['x-total'])
      setMaterials(res.data)
    }
  }, [page, pageLimit, search, sortBy, sortType, materialType, statusMAT])

  useEffect(() => {
    loadData()
  }, [loadData])

  const loadDataForExport = useCallback(async () => {
    let condition: any = { group: 'MATERIAL', status: '' }
    condition.export = 1
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (search || !_.isEmpty(materialType)) condition = { ...condition, search: search || materialType?.productTypeName || '' }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }
    if (statusMAT) condition = { ...condition, status: statusMAT.status === "ALL" ? '' : statusMAT.status }

    const res = await MaterialsApi.findAll(condition)
    if (res.status === 200) {
      setExportData(res.data)
    }
  }, [page, pageLimit, search, sortBy, sortType, materialType, statusMAT])

  useEffect(() => {
    loadDataForExport()
  }, [loadDataForExport])

  const loadProductTypes = (medSearch?: string) => {
    setMedTypeLoading(true)
    const condition: any = { group: 'MATERIAL', pageLimit: 10, status: 'ACTIVE', search: medSearch || '' }
    ProductTypesApi.findAll(condition)
      .then(({ data }) => setMaterialTypes(data))
      .catch((e) => { return })
      .finally(() => setMedTypeLoading(false))
  }

  const handleInputSearch = useCallback(
    _.debounce((medSearch?: string) => {
      loadProductTypes(medSearch)
      setPage(1)
    }, 1000), []);

  useEffect(() => {
    loadProductTypes()
  }, []);

  const onEdit = (data: any) => {
    window.location.href = `${routeName.material}/${data.productId}`
  }

  const onCreate = () => {
    window.location.href = routeName.material + '/create'
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const onActive = () => {
    if (id) {
      MaterialsApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('MATERIAL.MSG.UPDATE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('MATERIAL.MSG.UN_SUCCESS'))
        }
      })
    }
  }

  const onDelete = () => {
    if (id) {
      MaterialsApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('MATERIAL.MSG.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('MATERIAL.MSG.UN_SUCCESS'))
        }
      })
    }
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: '#', width: '80px' },
    { id: 'productTypeId', disablePadding: false, label: t('MATERIAL.TABLE.CELLS_MED.MATERIAL_TYPE'), width: '200px' },
    { id: 'productCode', disablePadding: false, label: t('MATERIAL.TABLE.CELLS_MED.MATERIAL_CODE'), width: '150px' },
    { id: 'productName', disablePadding: false, label: t('MATERIAL.TABLE.CELLS_MED.MATERIAL_NAME') },
    { id: 'price', disablePadding: false, label: t('MATERIAL.TABLE.CELLS_MED.PRICE'), width: '150px' },
    { id: 'status', disablePadding: false, label: t('MATERIAL.TABLE.CELLS_MED.STATUS'), width: '150px', align: 'center' },
    { id: 'updatedBy', disablePadding: false, label: t('MATERIAL.TABLE.CELLS_MED.UPDATED_BY'), width: '175px', align: 'center' },
    { id: 'action', disablePadding: false, label: 'Action', width: '80px' }
  ]

  const renderData = (objData: any, no: number, valueExportData: boolean) => {
    const num = valueExportData === true ? no + 1 : page * pageLimit - pageLimit + no + 1
    const { productId, productType, productCode, productName, salePrice, updatedBy, updatedAt, status: medStatus } = objData

    const colUpdateExport = {
      option: 'TEXT', align: 'left', label: `${updatedBy} ${dateTimeToView(updatedAt)}`
    }
    const renderColUpdate = valueExportData ? colUpdateExport : { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } }

    const statusBtnActive = medStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = medStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: productId,
      id: productId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: num },
        { option: 'TEXT', align: 'left', label: productType.productTypeName },
        { option: 'TEXT', align: 'left', label: productCode },
        { option: 'TEXT', align: 'left', label: productName },
        { option: 'TEXT', align: 'right', label: numberFormat(salePrice) },
        { option: 'STATUS', align: 'center', label: medStatus },
        { ...renderColUpdate },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <>
        {valueExportData === true && <TableRowExport {...objRenderData} /> ||
          <TableRowCommon
            {...objRenderData}
            onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
            oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
            onedit={() => onEdit(objRenderData.obj)}
            ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
          />
        }
      </>
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.productName)
  }

  const filter = [
    { label: `${t('INPUT.SEARCH')}: ${search || '-'}` },
    { label: `${t('MATERIAL.TEXT.TYPE')}: ${!_.isEmpty(materialType) ? materialType?.productTypeName : '-'}` }
  ]

  return (
    <div className={classes.root}>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`MATERIAL.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`MATERIAL.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { productName: name, status: t(`MATERIAL.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <Card className="border-0 h-100">
        <HeaderCard text={t('MATERIAL.HEADER.TITLE')} />
        <Card.Body>
          <Card.Title className={'mb-0 pl-xl-3'}>
            <Row>
              <Col lg={7}>
                <Row className="">
                  <Col sm={12} md={4} xl={4} className="pt-2">
                    <InputTextSearch
                      keyInput={'search'}
                      label={t('MATERIAL.TEXT.SEARCH')}
                      value={search}
                      onchange={(event) => {
                        setSearch(event.target.value)
                        setPage(1)
                      }}
                      onClear={(event) => {
                        setSearch('')
                        setPage(1)
                      }}
                    />
                  </Col>
                  <Col sm={12} md={4} xl={4} className="pl-md-0 pt-2">
                    <Box className='position-relative' sx={{ '.MuiOutlinedInput-input': { paddingLeft: '70px !important' } }}>
                      <Typography className='position-absolute top-50 translate-middle-y' sx={{ left: '12px', zIndex: 1 }}>{t('MATERIAL.TEXT.TYPE')}:</Typography>
                      <AutocompleteSelect
                        labelId="materialType"
                        noOptionsText={t('NOT_FOUND')}
                        options={materialTypes}
                        getOptionLabel={(option: any) => option.productTypeName || t('ALL')}
                        renderOption={(props, option: any) => (
                          <Box component="li" {...props}>
                            {option.productTypeName}
                          </Box>
                        )}
                        onchange={(e, value: any) => {
                          setPage(1)
                          setMaterialType(value)
                        }}
                        inputProps={{
                          onChange: (e: any) => {
                            setMaterialTypes([])
                            handleInputSearch(e.target.value)
                          }
                        }}
                        onOpen={(e: any) => loadProductTypes()}
                        value={!_.isEmpty(materialType) ? materialType : ''}
                        loading={medTypeLoading}
                        height={32}
                        disabledBorder
                        disableClearable={_.isEmpty(materialType)}
                      />
                    </Box>
                  </Col>
                  <Col sm={12} md={4} xl={4} className="pl-md-0 pt-2">
                    <Box className='position-relative' sx={{ '.MuiOutlinedInput-input': { paddingLeft: '70px !important' } }}>
                      <Typography className='position-absolute top-50 translate-middle-y' sx={{ left: '12px', zIndex: 1 }}>{t('MATERIAL.TEXT.STATUS')}:</Typography>
                      <AutocompleteSelect
                        labelId="statusMAT"
                        noOptionsText={t('NOT_FOUND')}
                        options={[
                          { status: 'ALL', name: t('ALL') },
                          { status: 'ACTIVE', name: t('MATERIAL.FILTER.ACTIVE') },
                          { status: 'INACTIVE', name: t('MATERIAL.FILTER.INACTIVE') }
                        ]}
                        getOptionLabel={(option: any) => option?.name || ''}
                        renderOption={(props, option: any) => (
                          <Box component="li" {...props}>
                            {t(`MATERIAL.TEXT.STATUS_${option.status}`)}
                          </Box>
                        )}
                        onchange={(e, value: any) => {
                          setPage(1)
                          setStatusMAT(value)
                        }}
                        onOpen={(e: any) => loadProductTypes()}
                        value={!_.isEmpty(statusMAT) ? statusMAT : ''}
                        height={32}
                        disabledBorder
                        disableClearable={_.isEmpty(statusMAT)}
                      />
                    </Box>
                  </Col>
                </Row>
              </Col>
              <Col lg={5} className="mt-2 mt-lg-auto">
                <div className="pr-xl-2 d-flex">
                  <div className='ml-auto'>
                    <ButtonExport headCells={headCells} portrait={false} filter={filter} fileName={t('MATERIAL.BUTTON.ITEM_MED')}
                      rowsData={exportData.map((val, i) => {
                        return renderData(val, i, true)
                      })} />
                  </div>
                  <div className='pl-3'>
                    <ButtonCustom mode="add" disabled={permissions.isCreate.disabled} onClick={() => onCreate()} textButton={t('MATERIAL.BUTTON.ADD_MED')} className="w-auto ml-auto d-flex mt-auto" btnStyle={{ height: '44px' }} />
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Title>

          <div className={'mt-3 pl-xl-3 pr-xl-2'}>
            <TableCustom
              page={page}
              pageLimit={pageLimit}
              sortType={sortType}
              sortBy={sortBy}
              onSort={onRequestSort}
              setPageLimit={handleChangeRowsPerPage}
              setPage={handleChangePage}
              rowCount={rowCount}
              headCells={headCells}
              rowsData={materials.map((val, i) => {
                return renderData(val, i, false)
              })}
              tableFixedWidth
              tableMinWidth={1300}
            />
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}
